import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getBaseUrl } from "../config";
import { getAuthApiHeaders } from "../../utils/common";
import { showSnackbar } from "./snackbar";

const baseUrl = getBaseUrl();

export const getRepEmails = createAsyncThunk(
  "repEmails/getRepEmails",
  async (mail, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${baseUrl}/rep_info`, {
        headers: {
          "Content-Type": "application/json",
          "use-case": "src",
          ...getAuthApiHeaders(),
          client: mail?.client ? mail.client : "UT",
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      dispatch(showSnackbar({ message: "Failed to load!", severity: "error" }));
      return rejectWithValue(error?.response?.statusText || "Request failed");
    }
  },
);

const repEmailsSlice = createSlice({
  name: "rep-emails",
  initialState: {
    isLoading: false,
    repDetails: [],
    rbdDetails: [],
    leaderDetails: [],
    selectedEmail: "",
    selectedName: "",
    productGroup: [],
    selectedProduct: "",
    isError: false,
    error: null,
    success: false,
  },
  reducers: {
    setSelectedEmail(state, action) {
      state.isLoading = true;
      state.selectedEmail = (action.payload || "").toLowerCase();
      state.isLoading = false;
    },
    setSelectedName(state, action) {
      state.isLoading = true;
      state.selectedName = action.payload || "";
      state.isLoading = false;
    },
    setProductGroup(state, action) {
      state.isLoading = true;
      state.productGroup = action.payload || [];
      state.isLoading = false;
    },
    setSelectedProduct(state, action) {
      state.isLoading = true;
      state.selectedProduct = action.payload || "";
      state.isLoading = false;
    },
    clearSelectedEmail(state) {
      state.selectedEmail = "";
      state.selectedName = "";
      state.productGroup = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRepEmails.pending, (state) => {
      state.isLoading = true;
      state.error = null;
      state.isError = false;
    });

    builder.addCase(getRepEmails.fulfilled, (state, action) => {
      state.success = true;
      state.isLoading = false;

      const rawData = action.payload || {};

      const parseArray = (value) => {
        if (typeof value === "string") {
          try {
            return JSON.parse(value.trim());
          } catch {
            return [];
          }
        }
        return Array.isArray(value) ? value : [];
      };

      state.repDetails = parseArray(rawData["Rep Details"]);
      state.rbdDetails = parseArray(rawData["RBD Details"]);
      state.leaderDetails = parseArray(rawData["Leader Details"]);

      const selectedEmail = (state.selectedEmail || "").toLowerCase().trim();

      if (!selectedEmail) {
        state.selectedName = "";
        state.productGroup = [];
        return;
      }

      const repMatches = state.repDetails.filter(
        (item) =>
          (item?.Sales_Rep_Email || "").toLowerCase().trim() === selectedEmail,
      );

      const rbdMatches = state.rbdDetails.filter(
        (item) =>
          (item?.Reg_Mgr_Email || "").toLowerCase().trim() === selectedEmail,
      );

      const leaderMatches = state.leaderDetails.filter(
        (item) =>
          (item?.LeaderEmail || "").toLowerCase().trim() === selectedEmail,
      );

      if (repMatches.length > 0) {
        state.selectedName = repMatches[0]?.Sales_Rep_Name || "";
        state.productGroup = [
          ...new Set(
            repMatches.map((item) => item?.productgroup).filter(Boolean),
          ),
        ];
      } else if (rbdMatches.length > 0) {
        state.selectedName = rbdMatches[0]?.Reg_Mgr_Name || "";
        state.productGroup = [
          ...new Set(
            rbdMatches.map((item) => item?.productgroup).filter(Boolean),
          ),
        ];
      } else if (leaderMatches.length > 0) {
        state.selectedName = leaderMatches[0]?.LeaderName || "";
        state.productGroup = [
          ...new Set(
            leaderMatches.map((item) => item?.productgroup).filter(Boolean),
          ),
        ];
      } else {
        state.selectedName = "";
        state.productGroup = [];
      }
    });

    builder.addCase(getRepEmails.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.error = action.payload;
      state.success = false;
    });
  },
});

export const {
  setSelectedEmail,
  setSelectedName,
  setProductGroup,
  setSelectedProduct,
  clearSelectedEmail,
} = repEmailsSlice.actions;

export default repEmailsSlice.reducer;
